import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { DeviceType, isAnepTemplate } from '../../../types/configTemplate';
import { DeviceTypeSelector } from '../../common/DeviceTypeSelector';
import { TemplateOwnerSelect } from '../TemplateOwnerSelect';
import { TemplateDetailState } from './useTemplateDetail';

/** The Overview tab: identity and usage, read or edited in place. */
export const TemplateOverviewTab: React.FC<{ state: TemplateDetailState }> = ({ state }) => {
  const { formatDateTime, formatNumber, t } = useI18n();
  const { template, isEditMode, isLoading, errors, setErrors, ownerName } = state;
  if (!template) return null;

  if (isEditMode) {
    return (
      <div className="tab-content">
        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('configTemplates.detail.identity')}</h3>
            <p>{t('configTemplates.detail.identityIntro')}</p>
          </header>
          <div className="cu-grid">
            <div className="cu-field cu-field-wide">
              <label htmlFor="editName">{t('configTemplates.templateName')} *</label>
              <input
                type="text"
                id="editName"
                data-testid="field-template-name-edit"
                value={state.name}
                onChange={(e) => state.setName(e.target.value)}
                className={`cu-input${errors.name ? ' is-invalid' : ''}`}
                disabled={isLoading}
              />
              {errors.name && <span className="cu-error" data-testid="error-template-name">{errors.name}</span>}
            </div>

            <div className="cu-field cu-field-wide">
              <label htmlFor="editDescription">{t('common.description')}</label>
              <textarea
                id="editDescription"
                data-testid="field-template-description-edit"
                className="cu-input"
                value={state.description}
                onChange={(e) => state.setDescription(e.target.value)}
                rows={2}
                disabled={isLoading}
              />
            </div>

            <div className="cu-field">
              <label htmlFor="editDeviceType">{t('devices.deviceType')}</label>
              <DeviceTypeSelector
                id="editDeviceType"
                className="cu-input"
                value={state.deviceType}
                onChange={(value) => {
                  state.setDeviceType(value as DeviceType);
                  if (!value) state.setCustomFields([]);
                }}
                includeEmpty={false}
              />
              {template.device_count > 0 && (
                <span className="cu-hint">{t('configTemplates.devicesUsing', { count: formatNumber(template.device_count) })}</span>
              )}
            </div>

            <div className="cu-field">
              <label htmlFor="editOwner">{t('configTemplates.ownerLabel')} *</label>
              <TemplateOwnerSelect
                id="editOwner"
                value={state.ownerId}
                onChange={(next) => {
                  state.setOwnerId(next);
                  if (errors.client_id) setErrors((prev) => ({ ...prev, client_id: undefined }));
                }}
                error={!!errors.client_id}
                disabled={isLoading}
              />
              {errors.client_id && <span className="cu-error">{errors.client_id}</span>}
            </div>
          </div>
        </section>

        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('configTemplates.detail.usage')}</h3>
            <p>{t('configTemplates.detail.usageIntro')}</p>
          </header>
          <div className="cu-grid">
            <div className="cu-field">
              <label htmlFor="editStatus">{t('configTemplates.detail.statusLabel')}</label>
              <select
                id="editStatus"
                className="cu-input"
                value={state.isActive ? 'active' : 'inactive'}
                onChange={(e) => state.setIsActive(e.target.value === 'active')}
                disabled={isLoading}
              >
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
            <div className="cu-field">
              <label className="cu-switch tpd-switch">
                <input
                  type="checkbox"
                  data-testid="field-template-default-edit"
                  checked={state.isDefault}
                  onChange={(e) => state.setIsDefault(e.target.checked)}
                  disabled={isLoading}
                />
                <span className="cu-switch-track" aria-hidden="true"><span className="cu-switch-thumb" /></span>
                <span className="cu-switch-text">
                  <strong>{t('configTemplates.defaultTemplate')}</strong>
                  <span className="cu-hint">{t('configTemplates.defaultHint')}</span>
                </span>
              </label>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const owner = ownerName(template.client_id);
  return (
    <div className="tab-content">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('configTemplates.detail.identity')}</h3>
          <p>{t('configTemplates.detail.identityIntro')}</p>
        </header>
        <dl className="tpd-facts">
          <div className="tpd-fact">
            <dt>{t('configTemplates.templateName')}</dt>
            <dd>{template.name}</dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('configTemplates.ownerLabel')}</dt>
            <dd className={isAnepTemplate(template.client_id) ? 'tpd-owner is-anep' : undefined}>{owner}</dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('devices.deviceType')}</dt>
            <dd><span className="type-badge tpl-type">{template.device_type}</span></dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('configTemplates.detail.defaultLabel')}</dt>
            <dd>{template.is_default ? t('common.yes') : t('common.no')}</dd>
          </div>
          <div className="tpd-fact tpd-fact-wide">
            <dt>{t('common.description')}</dt>
            <dd className={template.description ? undefined : 'tpd-muted'}>{template.description || t('configTemplates.detail.noDescription')}</dd>
          </div>
        </dl>
      </section>

      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('configTemplates.detail.usage')}</h3>
          <p>{t('configTemplates.detail.usageIntro')}</p>
        </header>
        <dl className="tpd-facts">
          <div className="tpd-fact">
            <dt>{t('configTemplates.devicesColumn')}</dt>
            <dd data-testid="template-device-count">
              {template.device_count > 0
                ? t('configTemplates.devicesUsing', { count: formatNumber(template.device_count) })
                : t('configTemplates.noDevicesUsing')}
            </dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('configTemplates.detail.statusLabel')}</dt>
            <dd>
              <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                {template.is_active ? t('common.active') : t('common.inactive')}
              </span>
            </dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('configTemplates.detail.created')}</dt>
            <dd>{formatDateTime(template.created_at)}</dd>
          </div>
          <div className="tpd-fact">
            <dt>{t('configTemplates.detail.updatedLabel')}</dt>
            <dd>{formatDateTime(template.updated_at)}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
};
